import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  BarChart3,
  Bell,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleCheck,
  CircleHelp,
  CirclePause,
  CirclePlus,
  CircleX,
  Clipboard,
  Clock3,
  Copy,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  FileCheck2,
  FileText,
  Filter,
  Folder,
  HandCoins,
  House,
  Info,
  GripVertical,
  LayoutDashboard,
  Link,
  LoaderCircle,
  LockKeyhole,
  LockKeyholeOpen,
  Mail,
  Menu,
  Pencil,
  Phone,
  Pin,
  Play,
  Plus,
  Printer,
  RefreshCw,
  Save,
  Search,
  Settings,
  Share2,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Tag,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  TrendingDown,
  TrendingUp,
  UserRound,
  UserRoundPlus,
  UsersRound,
  Video,
  Wallet,
  X,
  createElement,
} from 'lucide';
import { Icon, iconElement, type IconName } from './ui-icon';

const GLYPHS: Record<IconName, typeof Activity> = {
  activity: Activity,
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-up-right': ArrowUpRight,
  'bar-chart-3': BarChart3,
  bell: Bell,
  'briefcase-business': BriefcaseBusiness,
  'calendar-days': CalendarDays,
  check: Check,
  'check-check': CheckCheck,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  circle: Circle,
  'circle-check': CircleCheck,
  'circle-help': CircleHelp,
  'circle-pause': CirclePause,
  'circle-plus': CirclePlus,
  'circle-x': CircleX,
  clipboard: Clipboard,
  'clock-3': Clock3,
  copy: Copy,
  download: Download,
  ellipsis: Ellipsis,
  'external-link': ExternalLink,
  eye: Eye,
  'file-check-2': FileCheck2,
  'file-text': FileText,
  filter: Filter,
  folder: Folder,
  'hand-coins': HandCoins,
  house: House,
  info: Info,
  'grip-vertical': GripVertical,
  'layout-dashboard': LayoutDashboard,
  link: Link,
  'loader-circle': LoaderCircle,
  'lock-keyhole': LockKeyhole,
  'lock-keyhole-open': LockKeyholeOpen,
  mail: Mail,
  menu: Menu,
  pencil: Pencil,
  phone: Phone,
  pin: Pin,
  play: Play,
  plus: Plus,
  printer: Printer,
  'refresh-cw': RefreshCw,
  save: Save,
  search: Search,
  settings: Settings,
  'share-2': Share2,
  'shield-check': ShieldCheck,
  'sliders-horizontal': SlidersHorizontal,
  star: Star,
  tag: Tag,
  'thumbs-down': ThumbsDown,
  'thumbs-up': ThumbsUp,
  'trash-2': Trash2,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  'user-round': UserRound,
  'user-round-plus': UserRoundPlus,
  'users-round': UsersRound,
  video: Video,
  wallet: Wallet,
  x: X,
};

const markerSelector = 'span[data-w365-icon]';
const iconAttrs = {
  width: 20,
  height: 20,
  'stroke-width': 1.5,
  class: 'w365-icon',
  'aria-hidden': 'true',
  focusable: 'false',
};

export function mountIcons(root: ParentNode = document): void {
  if (typeof document === 'undefined' || !root) return;
  const markers = Array.from(root.querySelectorAll(markerSelector));
  if (root instanceof Element && root.matches(markerSelector)) markers.unshift(root);
  markers.forEach((marker) => {
    const name = marker.getAttribute('data-w365-icon') || '';
    if (!Object.prototype.hasOwnProperty.call(GLYPHS, name)) {
      throw new TypeError(`Unknown Wealth365 icon: ${name}`);
    }
    if (marker.getAttribute('data-w365-rendered') === name
        && marker.childElementCount === 1
        && marker.firstElementChild?.matches('svg.w365-icon')) {
      return;
    }
    // Keep the marker itself stable: IDs, focus and any action listeners belong
    // to their original element, never to a replacement generated SVG.
    marker.replaceChildren(createElement(GLYPHS[name as IconName], iconAttrs));
    marker.setAttribute('data-w365-rendered', name);
  });
}

let iconObserver: MutationObserver | null = null;

export function observeIcons(root: Node = document.documentElement): void {
  if (typeof MutationObserver === 'undefined' || !root || iconObserver) return;
  iconObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.type === 'attributes' && record.target instanceof Element) {
        mountIcons(record.target);
        return;
      }
      record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(markerSelector) || node.querySelector(markerSelector)) {
          mountIcons(node);
        }
      });
    });
  });
  iconObserver.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-w365-icon'],
  });
}

export { Icon as icon, iconElement };

declare global {
  interface Window {
    Wealth365Icons: {
      icon: typeof Icon;
      iconElement: typeof iconElement;
      mountIcons: typeof mountIcons;
    };
  }
}

window.Wealth365Icons = {
  icon: Icon,
  iconElement,
  mountIcons,
};

mountIcons();
observeIcons();
